import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import './App.css';
import './index.css';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import CustomerDashboard from './pages/Customer/Dashboard';
import CustomerSearch from './pages/Customer/Search';
import CustomerWarehouseDetail from './pages/Customer/WarehouseDetail';
import CustomerBookings from './pages/Customer/Bookings';
import ProviderDashboard from './pages/Provider/Dashboard';
import ProviderWarehouses from './pages/Provider/Warehouses';
import ProviderAddWarehouse from './pages/Provider/AddWarehouse';
import ProviderAddMHE from './pages/Provider/AddMHE';
import ProviderAddFleet from './pages/Provider/AddFleet';
import ProviderAddManpower from './pages/Provider/AddManpower';
import ProviderMHEList from './pages/Provider/MHEList';
import ProviderFleetList from './pages/Provider/FleetList';
import ProviderManpowerList from './pages/Provider/ManpowerList';
import AdminDashboard from './pages/Admin/Dashboard';
import AdminUsers from './pages/Admin/Users';
import AdminTransactions from './pages/Admin/Transactions';
import AdminWarehouses from './pages/Admin/Warehouses';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return null; // Initial loader handles this
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" />;
  }

  return children;
};

const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginPage />} />
      <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <RegisterPage />} />
      
      <Route
        path="/"
        element={
          user ? <Navigate to="/dashboard" /> : <LandingPage />
        }
      />
      <Route
        path="/dashboard"
        element={
          user ? (
            user.role === 'customer' ? <Navigate to="/customer/dashboard" /> :
            user.role === 'provider' ? <Navigate to="/provider/dashboard" /> :
            user.role === 'admin' ? <Navigate to="/admin/dashboard" /> :
            <Navigate to="/login" />
          ) : (
            <Navigate to="/login" />
          )
        }
      />

      {/* Customer Routes */}
      <Route path="/customer/dashboard" element={<ProtectedRoute allowedRoles={['customer']}><CustomerDashboard /></ProtectedRoute>} />
      <Route path="/customer/search" element={<ProtectedRoute allowedRoles={['customer']}><CustomerSearch /></ProtectedRoute>} />
      <Route path="/customer/search/warehouses" element={<ProtectedRoute allowedRoles={['customer']}><CustomerSearch /></ProtectedRoute>} />
      <Route path="/customer/search/mhe" element={<ProtectedRoute allowedRoles={['customer']}><CustomerSearch serviceType="mhe" /></ProtectedRoute>} />
      <Route path="/customer/search/fleet" element={<ProtectedRoute allowedRoles={['customer']}><CustomerSearch serviceType="fleet" /></ProtectedRoute>} />
      <Route path="/customer/search/manpower" element={<ProtectedRoute allowedRoles={['customer']}><CustomerSearch serviceType="manpower" /></ProtectedRoute>} />
      <Route path="/customer/warehouse/:id" element={<ProtectedRoute allowedRoles={['customer']}><CustomerWarehouseDetail /></ProtectedRoute>} />
      <Route path="/customer/bookings" element={<ProtectedRoute allowedRoles={['customer']}><CustomerBookings /></ProtectedRoute>} />

      {/* Provider Routes */}
      <Route path="/provider/dashboard" element={<ProtectedRoute allowedRoles={['provider']}><ProviderDashboard /></ProtectedRoute>} />
      <Route path="/provider/warehouses" element={<ProtectedRoute allowedRoles={['provider']}><ProviderWarehouses /></ProtectedRoute>} />
      <Route path="/provider/add-warehouse" element={<ProtectedRoute allowedRoles={['provider']}><ProviderAddWarehouse /></ProtectedRoute>} />
      <Route path="/provider/mhe" element={<ProtectedRoute allowedRoles={['provider']}><ProviderMHEList /></ProtectedRoute>} />
      <Route path="/provider/add-mhe" element={<ProtectedRoute allowedRoles={['provider']}><ProviderAddMHE /></ProtectedRoute>} />
      <Route path="/provider/fleet" element={<ProtectedRoute allowedRoles={['provider']}><ProviderFleetList /></ProtectedRoute>} />
      <Route path="/provider/add-fleet" element={<ProtectedRoute allowedRoles={['provider']}><ProviderAddFleet /></ProtectedRoute>} />
      <Route path="/provider/manpower" element={<ProtectedRoute allowedRoles={['provider']}><ProviderManpowerList /></ProtectedRoute>} />
      <Route path="/provider/add-manpower" element={<ProtectedRoute allowedRoles={['provider']}><ProviderAddManpower /></ProtectedRoute>} />

      {/* Admin Routes */}
      <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['admin']}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/transactions" element={<ProtectedRoute allowedRoles={['admin']}><AdminTransactions /></ProtectedRoute>} />
      <Route path="/admin/warehouses" element={<ProtectedRoute allowedRoles={['admin']}><AdminWarehouses /></ProtectedRoute>} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <AppRoutes />
          <Toaster position="top-right" />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;